"use client"

import type { Table } from "@tanstack/react-table"
import { Check, GripVertical, Settings2 } from "lucide-react"
import * as React from "react"
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from "@dnd-kit/core"
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

import { Button } from "../button"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../popover"
import { cn } from "../../../utils"

interface DataGridViewOptionsProps<TData>
    extends React.ComponentProps<typeof PopoverContent> {
    table: Table<TData>
    disabled?: boolean
    columnOrder?: string[]
    onColumnOrderChange?: (order: string[]) => void
}

interface SortableColumnItemProps {
    column: any
    isVisible: boolean
    onToggle: () => void
}

/**
 * Gets the display label for a column.
 * Priority:
 * 1. column.columnDef.meta.label (if defined)
 * 2. column.columnDef.header (if it's a string)
 * 3. Formatted column.id (snake_case to Title Case)
 */
function getColumnLabel(column: any): string {
    // Check meta.label first (custom label defined in column definition)
    const meta = column.columnDef?.meta as { label?: string; filterLabel?: string } | undefined
    if (meta?.label) {
        return meta.label
    }
    
    // Check meta.filterLabel as fallback
    if (meta?.filterLabel) {
        return meta.filterLabel
    }
    
    // Check if header is a plain string
    const header = column.columnDef?.header
    if (typeof header === 'string') {
        return header
    }
    
    // Fallback: format the column id (snake_case/camelCase to Title Case)
    const id = column.id as string
    return id
        .replace(/_/g, ' ')           // snake_case to spaces
        .replace(/([a-z])([A-Z])/g, '$1 $2')  // camelCase to spaces
        .replace(/\b\w/g, c => c.toUpperCase())  // Capitalize first letter of each word
}

function SortableColumnItem({ column, isVisible, onToggle }: SortableColumnItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: column.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                "relative flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm outline-none",
                "hover:bg-accent hover:text-accent-foreground",
                "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                isDragging && "z-50"
            )}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-accent/50 rounded touch-none flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
            >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <div 
                className="flex items-center gap-2 flex-1 cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation()
                    onToggle()
                }}
            >
                <span className={cn("truncate flex-1", !isVisible && "text-muted-foreground")}>
                    {getColumnLabel(column)}
                </span>
                <Check
                    className={cn(
                        "size-4 shrink-0",
                        isVisible ? "opacity-100" : "opacity-0",
                    )}
                />
            </div>
        </div>
    )
}

export function DataGridViewOptions<TData>({
    table,
    disabled,
    columnOrder: externalColumnOrder,
    onColumnOrderChange,
    ...props
}: DataGridViewOptionsProps<TData>) {
    const columnVisibility = table.getState().columnVisibility
    const [searchValue, setSearchValue] = React.useState("")

    const allColumns = table
        .getAllLeafColumns()
        .filter((column) => column.getCanHide())

    // Stabilize allColumnIds by serializing to a string key
    const allColumnIdsKey = allColumns.map((column) => column.id).join(',')
    const allColumnIds = React.useMemo(
        () => allColumnIdsKey.split(',').filter(Boolean),
        [allColumnIdsKey],
    )

    // Use external columnOrder if provided, otherwise use default order from table
    const [localColumnOrder, setLocalColumnOrder] = React.useState<string[]>([])

    // Use refs to break dependency cycles
    const localColumnOrderRef = React.useRef(localColumnOrder)
    localColumnOrderRef.current = localColumnOrder
    const onColumnOrderChangeRef = React.useRef(onColumnOrderChange)
    onColumnOrderChangeRef.current = onColumnOrderChange
    // Track whether we just pushed an update to the parent to avoid re-triggering
    const didPushToParentRef = React.useRef(false)

    React.useEffect(() => {
        // If we just pushed an order update to the parent, skip this effect run
        // (the parent re-rendered with the order we gave it — no reconciliation needed)
        if (didPushToParentRef.current) {
            didPushToParentRef.current = false
            return
        }

        // All column IDs including non-hideable ones (e.g. select checkbox)
        const allTableColumnIds = new Set(table.getAllLeafColumns().map(c => c.id))

        let hasMissingColumns = false
        const nextOrder = (() => {
            if (externalColumnOrder !== undefined) {
                const cleanedExternalOrder = externalColumnOrder.filter((id) =>
                    allTableColumnIds.has(id),
                )
                const missingIds = allColumnIds.filter(
                    (id) => !cleanedExternalOrder.includes(id),
                )
                hasMissingColumns = missingIds.length > 0 || cleanedExternalOrder.length !== externalColumnOrder.length
                return [...cleanedExternalOrder, ...missingIds]
            }

            const previousOrder = localColumnOrderRef.current.filter((id) =>
                allColumnIds.includes(id),
            )
            const missingIds = allColumnIds.filter((id) => !previousOrder.includes(id))
            return [...previousOrder, ...missingIds]
        })()

        setLocalColumnOrder((prev) => {
            if (
                prev.length === nextOrder.length &&
                prev.every((id, index) => id === nextOrder[index])
            ) {
                return prev
            }
            return nextOrder
        })

        // Only push back to parent when reconciliation actually changed something
        // (added missing columns or removed stale ones) — NOT on every render
        if (
            hasMissingColumns &&
            externalColumnOrder !== undefined &&
            onColumnOrderChangeRef.current
        ) {
            didPushToParentRef.current = true
            onColumnOrderChangeRef.current(nextOrder)
        }
    }, [externalColumnOrder, allColumnIds])

    // Sort columns by the current order
    const orderedColumns = React.useMemo(() => {
        if (localColumnOrder.length === 0) return allColumns

        const columnMap = new Map(allColumns.map(col => [col.id, col]))
        const ordered = localColumnOrder
            .map(id => columnMap.get(id))
            .filter((col): col is NonNullable<typeof col> => col !== undefined)

        // Add any new columns that aren't in the order yet
        const orderedIds = new Set(ordered.map(col => col.id))
        const newColumns = allColumns.filter(col => !orderedIds.has(col.id))

        return [...ordered, ...newColumns]
    }, [allColumns, localColumnOrder])

    // Filter columns based on search (search by display label)
    const filteredColumns = React.useMemo(() => {
        if (!searchValue) return orderedColumns
        
        const searchLower = searchValue.toLowerCase()
        return orderedColumns.filter(column => 
            getColumnLabel(column).toLowerCase().includes(searchLower)
        )
    }, [orderedColumns, searchValue])

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px movement before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        if (over && active.id !== over.id) {
            const oldIndex = localColumnOrder.indexOf(active.id as string)
            const newIndex = localColumnOrder.indexOf(over.id as string)

            const newOrder = arrayMove(localColumnOrder, oldIndex, newIndex)
            setLocalColumnOrder(newOrder)
            onColumnOrderChange?.(newOrder)
        }
    }

    return (
        <Popover modal>
            <PopoverTrigger asChild>
                <Button
                    aria-label="Toggle columns"
                    role="combobox"
                    variant="outline"
                    size="sm"
                    className="h-8 font-normal flex gap-2"
                    disabled={disabled}
                >
                    <Settings2 className="h-4 w-4" />
                    View
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-1" {...props}>
                <div className="flex flex-col gap-1">
                    <input
                        type="text"
                        placeholder="Search columns..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="h-9 px-3 py-1 text-sm border-0 border-b focus:outline-none focus:ring-0 focus:border-b-2 focus:border-indigo-500"
                    />
                    <div className="max-h-[300px] overflow-y-auto py-1">
                        {filteredColumns.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                No columns found.
                            </div>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                                autoScroll={false}
                            >
                                <SortableContext
                                    items={orderedColumns.map((column) => column.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {filteredColumns.map((column) => (
                                        <SortableColumnItem
                                            key={column.id}
                                            column={column}
                                            isVisible={column.getIsVisible()}
                                            onToggle={() => column.toggleVisibility(!column.getIsVisible())}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
